import React, { useMemo, useState } from 'react';

/**
 * Curva de crecimiento (peso o talla en el tiempo) dibujada en SVG, sin librerías.
 * `logs`: [{ date: 'YYYY-MM-DD', weight, height }]. Muestra la línea, los puntos y la variación entre el primer y el último registro.
 */
type Metric = 'weight' | 'height';

const toTime = (d: string) => new Date(`${String(d).slice(0, 10)}T00:00:00`).getTime();

export const GrowthChart: React.FC<{ logs: any[] }> = ({ logs }) => {
    const [metric, setMetric] = useState<Metric>('weight');
    const unit = metric === 'weight' ? 'kg' : 'cm';
    const color = metric === 'weight' ? '#10b981' : '#3b82f6';

    const pts = useMemo(() => (logs || [])
        .map(l => ({ t: toTime(l.date), v: Number(l[metric]) || 0 }))
        .filter(p => Number.isFinite(p.t) && p.v > 0)
        .sort((a, b) => a.t - b.t), [logs, metric]);

    const W = 560, H = 220, padL = 44, padR = 16, padT = 16, padB = 34;
    const tabs = (
        <div className="flex bg-slate-100 rounded-[7px] p-1 w-fit">
            {([['weight', 'Peso (kg)'], ['height', 'Talla (cm)']] as const).map(([k, l]) => (
                <button key={k} onClick={() => setMetric(k)} className={`px-4 py-1.5 rounded-[7px] text-[10px] font-black uppercase tracking-widest transition-all ${metric === k ? 'bg-white shadow text-slate-800' : 'text-slate-400'}`}>{l}</button>
            ))}
        </div>
    );

    if (pts.length < 2) {
        return (
            <div className="bg-white rounded-[7px] border border-slate-100 p-6">
                <div className="flex items-center justify-between mb-4">{tabs}</div>
                <p className="text-center text-xs font-bold text-slate-400 py-10 uppercase tracking-widest">
                    {pts.length === 0 ? 'Registra el primer control para ver la curva' : 'Con un segundo control aparece la curva'}
                </p>
            </div>
        );
    }

    const t0 = pts[0].t, t1 = pts[pts.length - 1].t;
    const vMin = Math.min(...pts.map(p => p.v)), vMax = Math.max(...pts.map(p => p.v));
    const span = vMax - vMin || vMax * 0.2 || 1;
    const lo = Math.max(0, vMin - span * 0.15), hi = vMax + span * 0.15;
    const x = (t: number) => padL + ((t - t0) / (t1 - t0 || 1)) * (W - padL - padR);
    const y = (v: number) => padT + (1 - (v - lo) / (hi - lo)) * (H - padT - padB);
    const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
    const area = `${line} L${x(t1).toFixed(1)},${H - padB} L${x(t0).toFixed(1)},${H - padB} Z`;
    const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => lo + (hi - lo) * f);
    const first = pts[0], last = pts[pts.length - 1];
    const delta = last.v - first.v;
    const fmtDate = (t: number) => new Date(t).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
    const fmtV = (v: number) => (metric === 'weight' ? v.toFixed(v < 10 ? 2 : 1) : v.toFixed(1));

    return (
        <div className="bg-white rounded-[7px] border border-slate-100 p-5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                {tabs}
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                    {fmtV(first.v)} → <span style={{ color }}>{fmtV(last.v)} {unit}</span>
                    <span className={`ml-2 ${delta >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>{delta >= 0 ? '+' : ''}{fmtV(delta)} {unit}</span>
                </p>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={`Curva de ${metric === 'weight' ? 'peso' : 'talla'}`}>
                {ticks.map((tv, i) => (
                    <g key={i}>
                        <line x1={padL} x2={W - padR} y1={y(tv)} y2={y(tv)} stroke="#e2e8f0" strokeWidth={1} strokeDasharray={i === 0 ? '0' : '3 4'} />
                        <text x={padL - 8} y={y(tv) + 3.5} textAnchor="end" fontSize={10} fontWeight={700} fill="#94a3b8">{fmtV(tv)}</text>
                    </g>
                ))}
                <path d={area} fill={color} opacity={0.12} />
                <path d={line} fill="none" stroke={color} strokeWidth={2.6} strokeLinejoin="round" strokeLinecap="round" />
                {pts.map((p, i) => (
                    <g key={i}>
                        <circle cx={x(p.t)} cy={y(p.v)} r={i === pts.length - 1 ? 5 : 3.5} fill="#fff" stroke={color} strokeWidth={2.4} />
                        <title>{`${fmtDate(p.t)} · ${fmtV(p.v)} ${unit}`}</title>
                    </g>
                ))}
                <text x={x(t0)} y={H - 10} textAnchor="start" fontSize={10} fontWeight={700} fill="#94a3b8">{fmtDate(t0)}</text>
                <text x={x(t1)} y={H - 10} textAnchor="end" fontSize={10} fontWeight={700} fill="#94a3b8">{fmtDate(t1)}</text>
            </svg>
        </div>
    );
};

export default GrowthChart;
